import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Avatar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  AccountBalanceWallet,
  Receipt,
  Store,
  Refresh,
  CalendarMonth,
  CreditCard,
  Savings,
  AccountBalance,
  ShoppingCart,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  BarChart,
  Bar,
} from 'recharts';
import {
  analyticsService,
  TimePeriod,
  AnalyticsSummary,
  CategoryAnalytics,
  TrendsAnalytics,
  TopMerchantsAnalytics,
  CreditBalances,
  SavingsAnalytics,
  AccountOverview,
} from '../services/analyticsService';
import { Transaction } from '../services/plaidService';
import {
  cachedAnalyticsByCategory,
  cachedAnalyticsTopMerchants,
  cachedAnalyticsCreditBalances,
  cachedAnalyticsSavings,
  cachedAnalyticsAccountOverview,
  cachedSpending,
  cachedIncome,
  invalidateAllData,
} from '../services/prefetch';

const CHART_COLORS = [
  '#6366f1', '#ec4899', '#10b981', '#f59e0b', '#3b82f6',
  '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

const getDateRangeForPeriod = (period: TimePeriod): { startDate: string; endDate: string } => {
  const endDate = new Date();
  const startDate = new Date();
  
  switch (period) {
    case 'week':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(endDate.getMonth() - 1);
      break;
    case 'quarter':
      startDate.setMonth(endDate.getMonth() - 3);
      break;
    case 'year':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    default:
      startDate.setMonth(endDate.getMonth() - 1);
  }
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const aggregateTransactionsToTrends = (
  spendingTransactions: Transaction[],
  incomeTransactions: Transaction[],
  period: TimePeriod
): TrendsAnalytics => {
  const dateMap = new Map<string, { spent: number; income: number; count: number }>();
  
  const getAggregationKey = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (period === 'week') {
      return dateStr;
    } else if (period === 'month') {
      return dateStr;
    } else {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}-01`;
    }
  };
  
  spendingTransactions.forEach((t) => {
    const key = getAggregationKey(t.date);
    const existing = dateMap.get(key) || { spent: 0, income: 0, count: 0 };
    existing.spent += Math.abs(t.amount);
    existing.count += 1;
    dateMap.set(key, existing);
  });
  
  incomeTransactions.forEach((t) => {
    const key = getAggregationKey(t.date);
    const existing = dateMap.get(key) || { spent: 0, income: 0, count: 0 };
    existing.income += Math.abs(t.amount);
    existing.count += 1;
    dateMap.set(key, existing);
  });
  
  const trends = Array.from(dateMap.entries())
    .map(([date, data]) => ({
      date,
      spent: data.spent,
      income: data.income,
      net: data.income - data.spent,
      transaction_count: data.count,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
  
  const totalSpent = trends.reduce((sum, t) => sum + t.spent, 0);
  const { startDate, endDate } = getDateRangeForPeriod(period);
  
  return {
    granularity: period === 'week' || period === 'month' ? 'daily' : 'monthly',
    months: period === 'year' ? 12 : period === 'quarter' ? 3 : 1,
    start_date: startDate,
    end_date: endDate,
    average_spent: trends.length > 0 ? totalSpent / trends.length : 0,
    trends,
  };
};

const Analytics: React.FC = () => {
  const [period, setPeriod] = useState<TimePeriod>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryAnalytics | null>(null);
  const [trendsData, setTrendsData] = useState<TrendsAnalytics | null>(null);
  const [merchantsData, setMerchantsData] = useState<TopMerchantsAnalytics | null>(null);
  const [creditData, setCreditData] = useState<CreditBalances | null>(null);
  const [savingsData, setSavingsData] = useState<SavingsAnalytics | null>(null);
  const [accountOverview, setAccountOverview] = useState<AccountOverview | null>(null);
  const [spendingTransactions, setSpendingTransactions] = useState<Transaction[]>([]);
  const [spendingPage, setSpendingPage] = useState(0);
  const [spendingRowsPerPage, setSpendingRowsPerPage] = useState(25);
  const [incomeTransactions, setIncomeTransactions] = useState<Transaction[]>([]);
  const [incomePage, setIncomePage] = useState(0);
  const [incomeRowsPerPage, setIncomeRowsPerPage] = useState(25);

  const fetchAnalytics = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = { period };
      const { startDate, endDate } = getDateRangeForPeriod(period);

      const [summaryRes, categoryRes, spendingRes, incomeRes, merchantsRes, creditRes, savingsRes, accountRes] = await Promise.all([
        // analyticsService.getSummary(params),  // Disabled
        Promise.resolve(null),
        cachedAnalyticsByCategory(params),
        cachedSpending(startDate, endDate),
        cachedIncome(startDate, endDate),
        cachedAnalyticsTopMerchants(params),
        cachedAnalyticsCreditBalances(),
        cachedAnalyticsSavings(),
        cachedAnalyticsAccountOverview(),
      ]);

      const trendsRes = aggregateTransactionsToTrends(
        spendingRes.transactions,
        incomeRes.transactions,
        period
      );

      setSummary(summaryRes);
      setCategoryData(categoryRes);
      setTrendsData(trendsRes);
      setMerchantsData(merchantsRes);
      setCreditData(creditRes);
      setSavingsData(savingsRes);
      setAccountOverview(accountRes);
      setSpendingTransactions(spendingRes.transactions || []);
      setSpendingPage(0);
      setIncomeTransactions(incomeRes.transactions || []);
      setIncomePage(0);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: TimePeriod | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const handleRefresh = async () => {
    await analyticsService.clearCache();
    invalidateAllData();
    fetchAnalytics(true);
  };

  const handleSpendingPageChange = (_: unknown, newPage: number) => {
    setSpendingPage(newPage);
  };

  const handleSpendingRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSpendingRowsPerPage(parseInt(event.target.value, 10));
    setSpendingPage(0);
  };

  const paginatedSpendingTransactions = spendingTransactions.slice(
    spendingPage * spendingRowsPerPage,
    spendingPage * spendingRowsPerPage + spendingRowsPerPage
  );

  const handleIncomePageChange = (_: unknown, newPage: number) => {
    setIncomePage(newPage);
  };

  const handleIncomeRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncomeRowsPerPage(parseInt(event.target.value, 10));
    setIncomePage(0);
  };

  const paginatedIncomeTransactions = incomeTransactions.slice(
    incomePage * incomeRowsPerPage,
    incomePage * incomeRowsPerPage + incomeRowsPerPage
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const SummaryCard = ({
    title,
    value,
    icon,
    comparison,
    color,
  }: {
    title: string;
    value: string;
    icon: React.ReactNode;
    comparison?: { value: number; label: string };
    color: string;
  }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color }}>
              {value}
            </Typography>
            {comparison && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {comparison.value >= 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: comparison.value >= 0 ? 'error.main' : 'success.main',
                    fontWeight: 600,
                  }}
                >
                  {formatPercent(comparison.value)} {comparison.label}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const CategoryTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 3,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {data.category}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(data.total_spent ?? 0)} ({(data.percentage ?? 0).toFixed(1)}%)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.transaction_count ?? 0} transactions
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const ChartTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const formattedLabel = label && label.includes('T') ? formatShortDate(label) : label;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 3,
          }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {formattedLabel}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={300} height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={400} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={400} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className="animate-fade-in">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Analytics
          </Typography>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Track your spending patterns and financial trends
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <CalendarMonth sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            Time Period:
          </Typography>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
          >
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="quarter">Quarter</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>
          {summary?.start_date && summary?.end_date && (
            <Chip
              label={`${formatShortDate(summary.start_date)} - ${formatShortDate(summary.end_date)}`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>
      </Card>

      {/* Financial Overview Section */}
      {accountOverview && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Financial Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccountBalance sx={{ color: 'white' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Net Worth
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                    {formatCurrency(accountOverview.net_worth ?? 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Assets</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>
                        {formatCurrency(accountOverview.total_assets ?? 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Liabilities</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>
                        {formatCurrency(accountOverview.total_liabilities ?? 0)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Savings sx={{ color: '#10b981' }} />
                    <Typography variant="body2" color="text.secondary">
                      Savings & Checking
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>
                    {formatCurrency(savingsData?.total_balance ?? 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Savings</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(savingsData?.savings_total ?? 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Checking</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(savingsData?.checking_total ?? 0)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CreditCard sx={{ color: '#f59e0b' }} />
                    <Typography variant="body2" color="text.secondary">
                      Credit Utilization
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#f59e0b' }}>
                    {creditData?.overall_utilization_percent ?? '0'}%
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Balance</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(creditData?.total_balance ?? 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Available</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(creditData?.total_available_credit ?? 0)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Spending Summary Section */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Spending Summary
      </Typography>
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SummaryCard
              title="Total Spending"
              value={formatCurrency(summary.total_spent ?? 0)}
              icon={<AttachMoney sx={{ color: '#ef4444' }} />}
              comparison={summary.spending_change_percent ? {
                value: parseFloat(summary.spending_change_percent),
                label: 'vs last period',
              } : undefined}
              color="#ef4444"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SummaryCard
              title="Total Income"
              value={formatCurrency(summary.total_income ?? 0)}
              icon={<AccountBalanceWallet sx={{ color: '#10b981' }} />}
              comparison={summary.income_change_percent ? {
                value: parseFloat(summary.income_change_percent),
                label: 'vs last period',
              } : undefined}
              color="#10b981"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SummaryCard
              title="Net Change"
              value={formatCurrency(summary.net ?? 0)}
              icon={(summary.net ?? 0) >= 0 ? <TrendingUp sx={{ color: '#10b981' }} /> : <TrendingDown sx={{ color: '#ef4444' }} />}
              color={(summary.net ?? 0) >= 0 ? '#10b981' : '#ef4444'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SummaryCard
              title="Transactions"
              value={(summary.transaction_count ?? 0).toLocaleString()}
              icon={<Receipt sx={{ color: '#6366f1' }} />}
              color="#6366f1"
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Spending by Category
              </Typography>
              {categoryData?.categories && categoryData.categories.length > 0 ? (
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData.categories}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="total_spent"
                        nameKey="category"
                        label={(props: any) =>
                          (props.percentage ?? 0) > 5 ? `${props.category ?? ''} ${(props.percentage ?? 0).toFixed(0)}%` : ''
                        }
                        labelLine={false}
                      >
                        {categoryData.categories.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CategoryTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                  <Typography color="text.secondary">No category data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Store sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Top Merchants
                </Typography>
              </Box>
              {merchantsData?.merchants && merchantsData.merchants.length > 0 ? (
                <List disablePadding>
                  {merchantsData.merchants.slice(0, 8).map((merchant, index) => (
                    <React.Fragment key={merchant.merchant}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
                            {index + 1}
                          </Typography>
                        </Box>
                        <ListItemText
                          primary={merchant.merchant ?? 'Unknown'}
                          secondary={`${merchant.transaction_count ?? 0} transactions`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(merchant.total_spent ?? 0)}
                          </Typography>
                        </Box>
                      </ListItem>
                      {index < merchantsData.merchants.slice(0, 8).length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No merchant data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Spending Trends
              </Typography>
              {trendsData?.trends && trendsData.trends.length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendsData.trends}>
                      <defs>
                        <linearGradient id="trendSpendingGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="trendIncomeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickFormatter={formatShortDate}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="spent"
                        name="Spending"
                        stroke="#ef4444"
                        fill="url(#trendSpendingGradient)"
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="income"
                        name="Income"
                        stroke="#10b981"
                        fill="url(#trendIncomeGradient)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No trend data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>


      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default Analytics;
